/**
 * <ChampionFigure> — l'un de ceux qui relaient le tuteur, en pied, côté élève (atom).
 *
 * Le livre V16 : « Côté parent il est petit et posé ; côté élève il est grand, et le jeu est
 * autour de lui » (chap. 07). `<ChampionAvatar>` dessine sa TÊTE dans un disque, pour une ligne ;
 * celui-ci le dessine ENTIER, pour la surface où il porte quelque chose (sa quête), et là où il parle
 * à la personne, avec sa bulle (`CLAUDE.md` § « Un champion qui parle », la candidature du tuteur).
 *
 * Un atome frère de `<Mascot>`, l'atome du parent, qui n'a ni Abou ni le type `ChampionName` de
 * l'espace élève. Les images sont celles que l'app embarque (`assets/champions/*_main.png`,
 * `*_happy.png`, et pour sa danse `assets/animations/<nom>/*-congratN.webp`) : ce que la marque y
 * dépose, sous les mêmes noms et dans les mêmes cadres (`championDance`).
 *
 * Les poses fixes partagent la même toile (512 × 853) : la largeur se déduit de la hauteur.
 * `pose="dancing"` le fait danser, comme l'ancien écran de fin d'activité : une de ses animations,
 * tirée une fois par champion, sur une toile plus large que haute (la boîte ne dépasse pas la largeur
 * de son parent, l'image s'y pose entière), lue par `expo-image`. En mouvement réduit, il est
 * content et fixe.
 *
 * `halo` pose derrière lui le halo de l'arène (« Halos corail, 22 % max », p. 35) : un disque
 * radial à la couleur de l'univers, qui part à 22 % et s'efface au bord. En SVG, comme la lueur
 * (`<Glow>`) : le natif n'a pas de dégradé radial. Son opacité ne passe que par `stopOpacity`.
 *
 * Un champion n'apparaît que là où il agit : jamais sur un écran vide, une erreur, une boîte de
 * dialogue.
 */
import { Image as ExpoImage } from "expo-image"
import React, { useId, useState } from "react"
import { Image, StyleSheet as RNStyleSheet, View } from "react-native"
import Svg, { Circle, Defs, RadialGradient, Stop } from "react-native-svg"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { useReduceMotion } from "../../hooks/useReduceMotion"
import { CHAMPIONS, type ChampionName } from "../Avatar/ChampionAvatar"
import { DANCE_RATIO, FIGURE_RATIO, championFigureSource, danceOf, type ChampionPose } from "./championDance"

export type { ChampionPose } from "./championDance"

/** « Halos corail, 22 % max » : l'opacité du centre, puis du palier, puis du bord. */
const HALO_STOPS = [0, 0.55, 1]
const HALO_ALPHAS = [0.22, 0.09, 0]

export type ChampionFigureProps = {
    name: ChampionName
    /**
     * `main` (défaut), `happy` quand il célèbre ce que l'élève vient de faire, `dancing` quand il le
     * danse (la fin d'une activité).
     */
    pose?: ChampionPose
    /** Hauteur en px. Défaut 120. La largeur suit la toile. */
    height?: number
    /** Le halo de l'arène derrière lui. Défaut `false`. */
    halo?: boolean
    testID?: string
}

export function ChampionFigure(props: ChampionFigureProps) {
    const { name, pose = "main", height = 120, halo = false, testID } = props
    const reduceMotion = useReduceMotion()
    // Une danse tirée par champion : un nouveau rendu (le live qui avance) n'en change pas, un autre
    // champion en tire une à lui.
    const [drawn, setDrawn] = useState(() => danceOf(null, name))
    const current = danceOf(drawn, name)
    if (current !== drawn) setDrawn(current)
    const figure = championFigureSource(name, pose, reduceMotion, current.dance)
    const width = Math.round(height * (figure.animated ? DANCE_RATIO : FIGURE_RATIO))

    return (
        <View
            testID={testID}
            accessibilityRole="image"
            accessibilityLabel={CHAMPIONS[name].label}
            style={styles.box(width, height)}
        >
            {halo && <Halo height={height} center={figure.animated ? DANCE_HALO_CENTER : HALO_CENTER} />}
            {figure.animated ? (
                // L'`<Image>` de react-native ne joue pas un WebP animé sur iOS (seul le GIF a un décodeur
                // animé, `RCTGIFImageDecoder`) : la danse passe par `expo-image`, comme l'ancien écran.
                // Pas une feuille Unistyles sur lui (composant non patché) : il remplit la boîte.
                <ExpoImage source={figure.source} style={RNStyleSheet.absoluteFill} contentFit="contain" />
            ) : (
                <Image source={figure.source} style={styles.image} resizeMode="contain" />
            )}
        </View>
    )
}

/** Le halo : un disque du diamètre de sa hauteur, centré sur son buste. Il déborde de sa boîte. */
function Halo(props: { height: number; center: number }) {
    const { height, center } = props
    const { theme } = useUnistyles()
    // Un identifiant par instance : sur le web, les dégradés partagent l'espace de noms du document.
    const gradientId = `halo-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`

    return (
        <View style={styles.haloRow(height, center)} pointerEvents="none">
            <View style={styles.halo(height)}>
                <Svg width="100%" height="100%">
                    <Defs>
                        <RadialGradient id={gradientId} cx="50%" cy="50%" r="50%">
                            {HALO_STOPS.map((offset, index) => (
                                <Stop
                                    key={offset}
                                    offset={offset}
                                    stopColor={theme.colors.role}
                                    stopOpacity={HALO_ALPHAS[index]}
                                />
                            ))}
                        </RadialGradient>
                    </Defs>
                    <Circle cx="50%" cy="50%" r="50%" fill={`url(#${gradientId})`} />
                </Svg>
            </View>
        </View>
    )
}

/** Le centre du halo, en fraction de la hauteur : le buste, un peu sous le milieu de la toile. */
const HALO_CENTER = 0.58
/** Dans une danse, le champion remplit la hauteur de sa toile : le halo se centre sur elle. */
const DANCE_HALO_CENTER = 0.5

const styles = StyleSheet.create(() => ({
    box: (width: number, height: number) => ({
        width,
        maxWidth: "100%" as const,
        height,
        flexShrink: 0,
    }),
    image: {
        width: "100%" as const,
        height: "100%" as const,
    },
    /** Une rangée de la largeur de la boîte, qui centre le disque quelle que soit la largeur finale. */
    haloRow: (height: number, center: number) => ({
        position: "absolute" as const,
        left: 0,
        right: 0,
        top: height * center - height / 2,
        height,
        alignItems: "center" as const,
    }),
    halo: (height: number) => ({
        width: height,
        height,
    }),
}))
